/**
 * 戒导航 · 表情面板弹出定位修正（jiedh/emoji）
 * =============================================================================
 * 设计依据：doc/表情资源自托管与表情面板优化设计方案.md 第四节
 *
 * 一、要修的是什么
 * -----------------------------------------------------------------------------
 * flarum/emoji 在 vendor 里用下面这段公式给表情面板定位
 * （vendor/flarum/emoji/js/src/forum/addComposerAutocomplete.tsx L162-181）：
 *
 *   let top = coordinates.top + 15;
 *   if (top + height > parent.height()) top = coordinates.top - height - 15; // ← 缺陷①
 *   ...
 *   top = Math.max(-(parent.offset().top - $(document).scrollTop()), top);   // ← 缺陷②
 *
 * 缺陷①：翻转条件用的是「编辑器自身高度 parent.height()」。Tiptap 富文本编辑器在
 *        只写一两行时高度约 190px，而下拉面板高度约 188px → `15 + 188 > 192` 恒成立
 *        → 必然走翻转分支，把面板顶到编辑器**上方**（实测 top = -152.75px，压住正文）。
 * 缺陷②：离屏钳制的参照物是「文档坐标」（parent.offset().top - scrollTop），本意是
 *        「别跑出屏幕顶部」，对「面板压在编辑器上方正文」这件事完全没有纠正能力。
 *
 * 二、怎么修（零侵入 vendor）
 * -----------------------------------------------------------------------------
 * inputListeners 是「按数组顺序依次调用」的（BasicEditorDriver / TiptapEditorDriver
 * 共用同一机制）。本模块在 TextEditor 的 buildEditorParams 里把回调 **push 到数组末尾**：
 *   · vendor 的定位回调先执行（它算出并写入内联 top/left）；
 *   · 我们的回调紧随其后执行，按「视口」重算并**覆盖**内联 top/left。
 *
 * 执行顺序为什么有保证：Extend::extend() 是「先注册者在内层、后注册者在外层」，
 * 即后注册的回调最后执行；而扩展启动顺序由 Flarum 的 ExtensionManager 拓扑排序决定，
 * 本包在 composer.json 里显式 require 了 `flarum/emoji` → 本包必然在 flarum/emoji
 * **之后**启动 → 本包的回调必然最后执行。详见 extend.ts 与设计方案 4.5。
 *
 * 三、修正后的定位原则
 * -----------------------------------------------------------------------------
 *   · 优先放在「光标行下方」；
 *   · 下方视口放不下时，改放「光标行上方」；
 *   · 上下都放不下（屏幕很矮）时，贴住视口底部（绝不再产生负 top）；
 *   · 水平方向钳制在父容器与视口内。
 *
 * 四、失效与回退
 * -----------------------------------------------------------------------------
 * 本模块依赖 vendor 的三处现有结构：`this.emojiDropdown`、`params.inputListeners`、
 * `.ComposerBody-emojiWrapper`（面板的 offsetParent）。任一处在升级后消失，本模块
 * 只会「静默不生效」（回到旧样式），不会让编辑器报错或不可用；确认失效后删除本文件
 * 与 packages/emoji 的前端入口即可。
 */

import { extend } from 'flarum/common/extend';
import TextEditor from 'flarum/common/components/TextEditor';

/** 面板与光标 / 视口边缘之间的最小间距（px）。 */
const GAP = 8;

/** 取不到计算样式行高时的兜底行高（px）。 */
const FALLBACK_LINE_HEIGHT = 24;

/**
 * 按「视口」重算表情面板位置并覆盖 vendor 写入的内联 top/left。
 *
 * @param component TextEditor 组件实例（用于取 this.emojiDropdown 与 composer.editor）
 */
function correctEmojiDropdownPosition(component) {
  // ---- 1) 前置守卫：面板不存在 / 未激活 / 不可见时一律不动 --------------------
  const dropdown = component?.emojiDropdown;
  if (!dropdown?.active) return;
  const $el = typeof dropdown.$ === 'function' ? dropdown.$() : null;
  if (!$el || !$el.length || !$el.is(':visible')) return;
  const el = $el[0];

  // ---- 2) 定位基准：面板的 offsetParent 就是 .ComposerBody-emojiWrapper ---------
  // （vendor 在 onbuild 里把 .TextEditor-editor 包进该 wrapper，wrapper 有 position: relative）
  // 因此我们算出的 top/left 都是「相对 wrapper」的值，与 vendor 的坐标系一致。
  const wrapper = el.offsetParent;
  if (!wrapper) return;
  const editor = component.attrs?.composer?.editor;
  if (!editor?.getCaretCoordinates || !editor?.getSelectionRange) return;
  const wrapperRect = wrapper.getBoundingClientRect();

  // 编辑器元素：Basic 驱动是 textarea、Tiptap 驱动是 ProseMirror 的 dom，
  // 两者都带 class `TextEditor-editor`，且 getCaretCoordinates() 的返回值
  // 正是「相对该元素」的坐标（见两个 Driver 的实现）。
  const editorEl = wrapper.querySelector('.TextEditor-editor') ?? wrapper;
  const editorRect = editorEl.getBoundingClientRect();

  // ---- 3) 光标位置：从「相对编辑器」换算成「相对 wrapper」 ----------------------
  const caret = editor.getCaretCoordinates(editor.getSelectionRange()[0]);
  const caretTop = caret.top + (editorRect.top - wrapperRect.top);
  const caretLeft = caret.left + (editorRect.left - wrapperRect.left);
  const lineHeight = parseFloat(window.getComputedStyle(editorEl).lineHeight) || FALLBACK_LINE_HEIGHT;

  // ---- 4) 垂直方向：下方优先 → 上方 → 贴视口底部 ------------------------------
  const h = el.offsetHeight;
  const w = el.offsetWidth;
  const viewportBottom = window.innerHeight - GAP;
  const below = caretTop + lineHeight + GAP; // 面板顶边在光标行下方时，相对 wrapper 的 top
  const above = caretTop - h - GAP; // 面板顶边在光标行上方时，相对 wrapper 的 top

  let top;
  if (wrapperRect.top + below + h <= viewportBottom) {
    // 下方放得下
    top = below;
  } else if (wrapperRect.top + above >= GAP) {
    // 上方放得下（且不出视口顶部）
    top = above;
  } else {
    // 上下都放不下：贴住视口底部（永远不小于 GAP，不会产生负 top）
    top = Math.max(GAP, viewportBottom - wrapperRect.top - h);
  }

  // ---- 5) 水平方向：钳制在「父容器」与「视口」的公共可用区间内 ------------------
  const minLeft = Math.max(GAP, GAP - wrapperRect.left);
  const maxLeft = Math.max(minLeft, Math.min(wrapperRect.width - w - GAP, window.innerWidth - wrapperRect.left - w - GAP));
  const left = Math.min(Math.max(caretLeft, minLeft), maxLeft);

  // ---- 6) 覆盖 vendor 写入的内联样式 ------------------------------------------
  $el.css({
    top: `${top}px`,
    left: `${left}px`
  });
}

/**
 * 前台启动入口：把定位修正回调追加到 TextEditor 的 inputListeners 末尾。
 */
export default function fixEmojiDropdownPosition() {
  extend(TextEditor.prototype, 'buildEditorParams', function (params) {
    // 取组件实例：vendor 也把回调 push 进同一个数组，我们紧随其后执行。
    const component = this;
    if (!params || !Array.isArray(params.inputListeners)) return;
    params.inputListeners.push(() => correctEmojiDropdownPosition(component));
  });
}